import { useMemo, useCallback, useState, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { useUserAvatar } from '../../hooks/useUserAvatar';
import { useDisambiguation } from '../../context/DisambiguationContext';
import { generateInitialsAvatar } from '../../utils/avatar';
import './MentionAutocomplete.css';

/**
 * Individual suggestion item for mention autocomplete
 * Renders a member with avatar and display name
 *
 * @param {Object} props
 * @param {Object} props.member - Room member object with userId, name, rawDisplayName
 * @param {number} props.index - Index of this item in the suggestions list
 * @param {boolean} props.isSelected - Whether this item is currently selected
 * @param {Function} props.onSelect - Callback when this item is clicked
 */
const MentionSuggestionItem = memo(({ member, index, isSelected, onSelect }) => {
  const { needsDisambiguation } = useDisambiguation();

  // Extract localpart from userId (e.g., "@mat_user2:server.com" → "mat_user2")
  const localpart = member.userId ? member.userId.split(':')[0].slice(1) : '';

  // Get display name - prefer rawDisplayName, then name, then localpart
  const displayName = member.rawDisplayName || member.name || localpart || member.userId;

  // Check if this member needs disambiguation (duplicate display name globally)
  const showFullMxid = needsDisambiguation(member.userId);

  // For avatar, use the display name
  const { avatarUrl } = useUserAvatar(member.userId, displayName, 24);
  const [avatarError, setAvatarError] = useState(false);

  // Reset avatar error when member changes (fixes bug with memoized component reuse)
  useEffect(() => {
    setAvatarError(false);
  }, [member.userId]);

  // Generate initials avatar as fallback
  const fallbackAvatar = useMemo(
    () => generateInitialsAvatar(displayName, 24),
    [displayName],
  );

  // Handle avatar load error
  const handleAvatarError = useCallback(() => {
    setAvatarError(true);
  }, []);

  // Use onMouseDown to prevent focus from moving away from input
  const handleMouseDown = useCallback((event) => {
    event.preventDefault(); // Prevents focus loss from input
    onSelect(member);
  }, [member, onSelect]);

  // Keyboard support for direct focus scenarios
  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(member);
    }
  }, [member, onSelect]);

  // Determine which avatar to show
  const avatarSrc = avatarError ? fallbackAvatar : (avatarUrl || fallbackAvatar);

  return (
    <div
      id={`mention-suggestion-${index}`}
      className={`mention-suggestion-item ${isSelected ? 'selected' : ''}`}
      onMouseDown={handleMouseDown}
      onKeyDown={handleKeyDown}
      role="option"
      aria-selected={isSelected}
      tabIndex={-1} // Prevent tab focus - keyboard navigation is handled by parent
    >
      <img
        src={avatarSrc}
        alt=""
        className="mention-suggestion-avatar"
        onError={handleAvatarError}
      />
      <div className="mention-suggestion-info">
        <div className="mention-suggestion-top-line">
          <span className="mention-suggestion-name">{displayName}</span>
          {/* Always show @localpart so users know what mention will be created */}
          <span className="mention-suggestion-userid">@{localpart}</span>
        </div>
        {showFullMxid && (
          <div className="mention-suggestion-mxid">{member.userId}</div>
        )}
      </div>
    </div>
  );
});

MentionSuggestionItem.displayName = 'MentionSuggestionItem';

MentionSuggestionItem.propTypes = {
  member: PropTypes.shape({
    userId: PropTypes.string.isRequired,
    name: PropTypes.string,
    rawDisplayName: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};

MentionSuggestionItem.defaultProps = {
  isSelected: false,
};

export default MentionSuggestionItem;
